import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Archive, Replace, Edit2 } from "lucide-react";
import { API, api, formatApiError, tokenStore } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const STATUS_LABEL = { valid: "Válida", expiring_soon: "Próxima a caducar", expired: "Caducada", archived: "Archivada" };
const STATUS_CLS = {
  valid: "bg-emerald-100 text-emerald-800",
  expiring_soon: "bg-amber-100 text-amber-800",
  expired: "bg-red-100 text-red-800",
  archived: "bg-slate-100 text-slate-500",
};

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; } }

export default function EvidenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [ev, setEv] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", collected_at: "", validity_period_days: "", tags: "" });

  const canEdit = ["owner", "admin", "compliance_manager"].includes(user?.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/evidences/${id}`); setEv(data); setForm({
      title: data.title, description: data.description || "",
      collected_at: data.collected_at?.slice(0, 10) || "",
      validity_period_days: data.validity_period_days || "",
      tags: (data.tags || []).join(", "),
    }); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/evidences"); }
  }, [id, navigate]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/evidences/${id}`);
        if (cancelled) return;
        setEv(data);
        setForm({
          title: data.title, description: data.description || "",
          collected_at: data.collected_at?.slice(0, 10) || "",
          validity_period_days: data.validity_period_days || "",
          tags: (data.tags || []).join(", "),
        });
      } catch (err) {
        if (!cancelled) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/evidences"); }
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  if (!ev) return <div className="text-slate-500">Cargando evidencia...</div>;

  const download = async () => {
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/evidences/${id}/download`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error("Descarga fallida.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = ev.file_name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const replace = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/evidences/${id}/replace-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEv(data); toast.success("Fichero reemplazado.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const archive = async () => {
    if (!window.confirm("¿Archivar esta evidencia? El fichero se conserva 90 días.")) return;
    try { const { data } = await api.delete(`/evidences/${id}`); setEv(data); toast.success("Archivada."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        title: form.title, description: form.description,
        collected_at: form.collected_at ? new Date(form.collected_at).toISOString() : null,
        validity_period_days: form.validity_period_days ? parseInt(form.validity_period_days, 10) : null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const { data } = await api.patch(`/evidences/${id}`, payload);
      setEv(data); setEdit(false); toast.success("Actualizada.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="evidence-detail-page" className="max-w-4xl">
      <Button variant="ghost" onClick={() => navigate("/evidences")} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Evidencias</Button>

      <div className="flex items-center gap-3">
        <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">{ev.title}</h1>
        <Badge className={STATUS_CLS[ev.status]}>{STATUS_LABEL[ev.status]}</Badge>
        <span className="text-[12px] text-slate-400">v{ev.file_version}</span>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={download} data-testid="btn-download"><Download className="h-3.5 w-3.5 mr-1" /> Descargar</Button>
        {canEdit && ev.status !== "archived" && (
          <>
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="btn-replace-file">
              <Replace className="h-3.5 w-3.5 mr-1" /> Reemplazar fichero
            </Button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={replace} />
            <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="btn-edit-metadata"><Edit2 className="h-3.5 w-3.5 mr-1" /> Editar metadatos</Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={archive} data-testid="btn-archive"><Archive className="h-3.5 w-3.5 mr-1" /> Archivar</Button>
          </>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Información</div>
          <Row k="Tipo" v={ev.type === "manual_upload" ? "Subida manual" : ev.type} />
          <Row k="Recogida" v={fmt(ev.collected_at)} />
          <Row k="Caduca" v={ev.expires_at ? fmt(ev.expires_at) : "No caduca"} />
          <Row k="Validez (días)" v={ev.validity_period_days || "—"} />
          <Row k="Subido" v={fmt(ev.uploaded_at)} />
          <Row k="Última actualización" v={fmt(ev.last_updated_at)} />
          <Row k="Tamaño" v={`${(ev.file_size_bytes / 1024 / 1024).toFixed(2)} MB`} />
          <Row k="Tipo MIME" v={ev.file_mime_type} />
          <div className="flex flex-wrap gap-1 mt-3">
            {(ev.tags || []).map((t) => <span key={t} className="text-[11px] border border-slate-200 rounded px-2 py-0.5">{t}</span>)}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Controles asociados ({(ev.related_control_ids || []).length})</div>
          <div className="space-y-1 text-[13px]">
            {(ev.related_control_ids || []).map((cid) => (
              <div key={cid} className="font-mono text-slate-700" data-testid={`assoc-ctrl-${cid}`}>{cid.slice(0, 8)}…</div>
            ))}
          </div>
          <p className="text-[12px] text-slate-400 mt-3">Para asociar/desasociar abre la evidencia desde la pantalla del control.</p>
        </div>
      </div>

      {ev.description && (
        <div className="mt-4 bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Descripción</div>
          <div className="text-[14px] text-slate-700 whitespace-pre-wrap">{ev.description}</div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={(e) => e.target === e.currentTarget && setEdit(false)}>
          <div className="bg-white rounded-md p-6 max-w-lg w-full">
            <h2 className="text-[16px] font-semibold mb-4">Editar metadatos</h2>
            <div className="space-y-3">
              <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title" /></div>
              <div><Label>Descripción</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fecha obtención</Label><Input type="date" value={form.collected_at} onChange={(e) => setForm({ ...form, collected_at: e.target.value })} data-testid="edit-collected" /></div>
                <div><Label>Validez (días)</Label><Input type="number" value={form.validity_period_days} onChange={(e) => setForm({ ...form, validity_period_days: e.target.value })} placeholder="Vacío = no caduca" data-testid="edit-validity" /></div>
              </div>
              <div><Label>Etiquetas</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="rgpd, q1-2026" data-testid="edit-tags" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setEdit(false)}>Cancelar</Button>
              <Button onClick={saveEdit} data-testid="edit-save">Guardar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between text-[13px] py-0.5"><span className="text-slate-500">{k}</span><span className="text-slate-800 font-medium">{v ?? "—"}</span></div>;
}
